import axios, { AxiosError } from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config';
import { tokenManager } from '../../utils/tokenManager';
import { AppDispatch } from '../store';
import { setLoading, setError, setEnrollments, addEnrollment } from '../slices/enrollmentSlice';

// Create a single axios instance
const api = axios.create({
  baseURL: config.API_BASE_URL,
  withCredentials: true,
});

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const getTokenWithRetry = async (retries = 0): Promise<string | null> => {
  try {
    const token = await tokenManager.getToken();
    if (token) {
      return token;
    }
    if (retries < MAX_RETRIES) {
      console.log(`Token not found, retrying in ${RETRY_DELAY}ms...`);
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      return getTokenWithRetry(retries + 1);
    }
    console.log('Max retries reached, no token found');
    return null;
  } catch (error) {
    console.error('Error in getTokenWithRetry:', error);
    return null;
  }
};

// Add an interceptor to include the JWT and auth type in every request
api.interceptors.request.use(
  async (config) => {
    const token = await getTokenWithRetry();
    const authType = tokenManager.getAuthType();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    if (authType) {
      config.headers['X-Auth-Type'] = authType.toLowerCase();
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add an interceptor for logging responses
api.interceptors.response.use(response => {
  return response
}, error => {
  return Promise.reject(error)
});

// Action Types
export const CLEAR_ALL_ENROLLMENT_MESSAGES = 'CLEAR_ALL_ENROLLMENT_MESSAGES';
export const CLEAR_ENROLLMENT_ERROR = 'CLEAR_ENROLLMENT_ERROR';

// Action Creators
export const clearAllEnrollmentMessages = () => ({
  type: CLEAR_ALL_ENROLLMENT_MESSAGES
});

export const clearEnrollmentError = () => ({
  type: CLEAR_ENROLLMENT_ERROR
});

// Enrollment Actions
export const registerEnrollmentDates = (enrollmentData: any) => async (dispatch: AppDispatch) => {
  try {
    const token = await getTokenWithRetry();
    const authType = tokenManager.getAuthType();

    if (!token) {
      throw new Error('No authentication token available');
    }

    const response = await api.post('/enrollment-dates', {
      employerName: enrollmentData.employerName,
      enrollmentStartDate: enrollmentData.enrollmentStartDate?.toISOString().split('T')[0],
      enrollmentEndDate: enrollmentData.enrollmentEndDate?.toISOString().split('T')[0],
      year: enrollmentData.year
    }, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Auth-Type': authType,
      },
    });

    dispatch(addEnrollment(response.data));
    return { success: true };
  } catch (error) {
    console.error('Error in registerEnrollmentDates:', error);
    
    // Handle Axios error with proper typing
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<any>;
      
      // Check if it's a 409 conflict error
      if (axiosError.response?.status === 409) {
        const errorMessage = axiosError.response.data.error;
        dispatch(setError(errorMessage));
        return { success: false, error: errorMessage };
      }
      
      // Handle other error responses
      const errorMessage = axiosError.response?.data?.error || axiosError.message;
      dispatch(setError(errorMessage));
      return { success: false, error: errorMessage };
    }

    // Handle non-Axios errors
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
    dispatch(setError(errorMessage));
    return { success: false, error: errorMessage };
  }
};

export const fetchEnrollmentDates = () => async (dispatch: AppDispatch) => {
  try {
    const token = await getTokenWithRetry();
    const authType = tokenManager.getAuthType();

    if (!token) {
      throw new Error('No authentication token available');
    }

    const response = await api.get('/enrollment-dates', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Auth-Type': authType,
      },
    });

    dispatch(setEnrollments(response.data));
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error in fetchEnrollmentDates:', error);
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
    dispatch(setError(errorMessage));
    return { success: false, error: errorMessage };
  }
};

// Export action types
export type EnrollmentActionTypes =
  ReturnType<typeof clearAllEnrollmentMessages> |
  ReturnType<typeof clearEnrollmentError> |
  ReturnType<typeof setError>;